import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Globe, Loader2, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import ImageUpload from '../components/ImageUpload';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const PageEditor = () => {
  const { user, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get('id');

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!postId);
  const [showSettings, setShowSettings] = useState(false);

  const [page, setPage] = useState({
    title: 'New Page', slug: '', status: 'published', post_type: 'page',
    category: 'general', og_image: '', featured_image: '', content: '',
  });

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (postId && user) {
      const token = getToken();
      axios.get(`${API_URL}/api/admin/post?id=${postId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      .then(res => { setPage(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load page'); setLoading(false); });
    }
  }, [postId, user, getToken]);

  const updateField = (field, value) => {
    setPage(prev => ({ ...prev, [field]: value }));
  };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80);

  const handleTitleChange = (value) => {
    updateField('title', value);
    if (!postId || !page.slug) updateField('slug', autoSlug(value));
  };

  const savePage = async (status) => {
    if (!page.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const token = getToken();
      const headers = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
      const payload = { ...page, status: status || page.status, slug: page.slug || autoSlug(page.title) };

      if (postId) {
        await axios.put(`${API_URL}/api/admin/post?id=${postId}`, payload, headers);
        toast.success('Page saved');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/posts`, payload, headers);
        toast.success('Page created');
        navigate(`/admin/page?id=${res.data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5]">
        <Loader2 size={32} className="animate-spin text-[#9B1E37]" />
      </div>
    );
  }

  if (!user) return null;

  // Determine hero mode for preview
  const hasFullWidthHero = !!page.featured_image;
  const heroImage = page.og_image || page.featured_image;

  return (
    <div className="min-h-screen bg-[#e8e8e8]" data-testid="page-editor">
      <Toaster position="top-right" richColors />

      {/* Editor Toolbar */}
      <div className="sticky top-0 z-[60] bg-[#2d2d2d] border-b border-[#444] shadow-lg">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white/70 hover:text-white hover:bg-white/10">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <span className="text-white/50 text-sm">Editing:</span>
            <span className="text-white font-medium text-sm">{page.title || 'Untitled'}</span>
            {page.slug && <span className="text-white/30 text-xs">/{page.slug}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} className="text-white/70 hover:text-white hover:bg-white/10">
              {showSettings ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
              {showSettings ? 'Hide Settings' : 'Page Settings'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => savePage('draft')} disabled={saving} className="text-white/70 hover:text-white hover:bg-white/10">
              <Save size={14} className="mr-1" /> Draft
            </Button>
            <Button size="sm" onClick={() => savePage('published')} disabled={saving} className="bg-[#9B1E37] hover:bg-[#7a1630] text-white">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Globe size={14} className="mr-1" />}
              Publish
            </Button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-[#363636] border-t border-[#444] px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-5xl">
              <div className="space-y-1">
                <Label className="text-white/60 text-xs">Page Title</Label>
                <Input value={page.title} onChange={(e) => handleTitleChange(e.target.value)} className="bg-[#2d2d2d] border-[#555] text-white h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-white/60 text-xs">URL Slug</Label>
                <Input value={page.slug} onChange={(e) => updateField('slug', e.target.value)} className="bg-[#2d2d2d] border-[#555] text-white h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-white/60 text-xs">Status</Label>
                <select value={page.status} onChange={(e) => updateField('status', e.target.value)} className="w-full bg-[#2d2d2d] border border-[#555] text-white h-9 px-2 rounded text-sm">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-white/60 text-xs">Hero Mode</Label>
                <select
                  value={page.featured_image ? 'fullwidth' : 'overlay'}
                  onChange={(e) => {
                    if (e.target.value === 'overlay') updateField('featured_image', '');
                  }}
                  className="w-full bg-[#2d2d2d] border border-[#555] text-white h-9 px-2 rounded text-sm"
                >
                  <option value="overlay">Red Overlay (standard)</option>
                  <option value="fullwidth">Full-Width Image (no overlay)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mt-4">
              <div className="space-y-1">
                <Label className="text-white/60 text-xs">
                  {page.featured_image ? 'Full-Width Hero Image' : 'Hero Background Image (behind red overlay)'}
                </Label>
                <ImageUpload
                  value={page.featured_image || page.og_image}
                  onChange={(url) => {
                    if (page.featured_image) {
                      updateField('featured_image', url);
                    } else {
                      updateField('og_image', url);
                    }
                  }}
                  getToken={getToken}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== LIVE PREVIEW ===== */}
      <div className="max-w-[1200px] mx-auto my-6 shadow-2xl rounded-lg overflow-hidden bg-white" data-testid="page-preview">

        {/* Real Header */}
        <div className="pointer-events-none">
          <TopBar />
          <Header config={STATIC_HEADER} />
        </div>

        {/* Hero Section — Editable */}
        {hasFullWidthHero ? (
          /* Full-width image hero mode */
          <div className="relative group cursor-pointer" onClick={() => setShowSettings(true)}>
            {page.featured_image ? (
              <img src={page.featured_image} alt={page.title} className="w-full block" style={{ maxHeight: '60vh', objectFit: 'cover' }} />
            ) : (
              <div className="h-48 bg-slate-200 flex items-center justify-center">
                <div className="text-center text-slate-400">
                  <ImageIcon size={32} className="mx-auto mb-2" />
                  <p className="text-sm">Click to set full-width hero image</p>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 bg-black/60 text-white text-sm px-3 py-1.5 rounded transition-opacity">Click to edit hero image</span>
            </div>
          </div>
        ) : (
          /* Standard red overlay hero */
          <div className="relative group cursor-pointer" onClick={() => setShowSettings(true)}>
            <div className="relative" style={{ minHeight: 200 }}>
              <div className="absolute inset-0" style={{
                backgroundColor: '#9E1B32',
                ...(page.og_image ? { backgroundImage: `url('${page.og_image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {})
              }} />
              <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
              <div className="relative z-10 py-16 sm:py-20">
                <div className="text-center max-w-3xl mx-auto px-4">
                  <input
                    value={page.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="text-3xl sm:text-4xl font-bold text-white text-center w-full bg-transparent border-none outline-none placeholder-white/50"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    placeholder="Page Title"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6" />
                </div>
              </div>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="bg-black/60 text-white text-xs px-2 py-1 rounded">Click to change background</span>
            </div>
          </div>
        )}

        {/* Content Body — Rich Text Editor */}
        <div className="px-4 sm:px-8 lg:px-16 py-10 max-w-4xl mx-auto">
          <RichTextEditor
            content={page.content}
            onChange={(html) => updateField('content', html)}
            getToken={getToken}
            placeholder="Start writing your page content..."
          />
        </div>

        {/* Real Footer */}
        <div className="pointer-events-none">
          <Footer config={STATIC_FOOTER} />
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="h-8" />
    </div>
  );
};

export default PageEditor;
